import React, { useState } from 'react';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, Paper, TextField, Button, Typography, Alert, Link, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for URL parameters
  const urlError = searchParams.get('error');
  const urlMessage = searchParams.get('message');

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  React.useEffect(() => {
    // Prevent scrolling on mount
    document.body.style.overflow = 'hidden';
    // Re-enable scrolling on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);
      await signIn({ email, password });
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      bgcolor: 'background.default',
      overflow: 'hidden'
    }}>
      <Paper sx={{ p: 4, maxWidth: 400, width: '100%', mt: -6 }} data-ui="auth-login-container">
        <Typography variant="h5" component="h1" gutterBottom>
          Login
        </Typography>
        {(error || urlError) && <Alert severity="error" sx={{ mb: 2 }} data-ui="feedback-error-alert">{error || urlError}</Alert>}
        {urlMessage && <Alert severity="success" sx={{ mb: 2 }} data-ui="feedback-success-alert">{urlMessage}</Alert>}
        <form onSubmit={handleSubmit} data-ui="auth-login-form">
          <TextField
            data-ui="auth-email-input"
            label="Email"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            data-ui="auth-password-input"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    data-ui="auth-password-toggle"
                    aria-label="toggle password visibility"
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            data-ui="btn-login-submit"
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
          >
            Login
          </Button>
        </form>

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Link component={RouterLink} to="/forgot-password" variant="body2" data-ui="auth-forgot-password-link">
            Forgot your password?
          </Link>
        </Box>

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Don't have an account?{' '}
            <Link component={RouterLink} to="/signup" data-ui="auth-signup-link">
              Sign up
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
